import React, { useCallback, useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useHistory } from 'react-router';
import { saveMatrix, fetchMatrix, createMatrix } from 'actions/matrices';
import classNames from 'classnames';
import StickySideModal from '../StickySideModal';
import ModalFooter from '../ModalFooter';
import Icon from 'components/Icon';
import ButtonNew from 'components/inputs/ButtonNew';
import './NewMatrixModal.scss';
import {
  duplicateMatrix,
  createMatrix as createNewMatrix,
} from 'constants/matrices';
import { openLoginPrompt } from 'actions/loginPrompt';

const NewMatrixModal = ({ className, publicMatrices, onCancel }) => {
  const history = useHistory();
  const dispatch = useDispatch();

  const { user } = useSelector(state => state.auth);

  let [selected, setSelected] = useState(null);

  const onDuplicateMatrix = useCallback(
    matrix => {
      const duplicate = duplicateMatrix(matrix, user);

      dispatch(createMatrix(duplicate));
      dispatch(saveMatrix(duplicate.id));

      const urlPath = matrix?.parentId
        ? `/matrices/${duplicate?.parentId}/experiments/${duplicate?.id}/edit`
        : `/matrices/${duplicate?.id}/edit`;

      // We have the matrix, but fetch anyway since it's all
      // saved and hasn't hit the loading list
      dispatch(fetchMatrix(duplicate.id));

      history.push(urlPath);
    },
    [dispatch, history, user]
  );

  const onCreateNewMatrix = useCallback(
    guided => {
      const newMatrix = createNewMatrix(user);
      dispatch(createMatrix(newMatrix));
      dispatch(saveMatrix(newMatrix.id));
      dispatch(fetchMatrix(newMatrix.id));
      let url = `/matrices/${newMatrix.id}`;
      if (guided) {
        url = `${url}/new`;
      } else {
        url = `${url}/edit`;
      }

      setTimeout(() => history.push(url), 150);
    },
    [dispatch, history, user]
  );

  const newMatrixOptions = [
    {
      name: 'Basic Builder',
      description: 'Add nodes and connections to create a matrix',
      type: 'blank',
    },
    {
      name: 'Guided Builder',
      description: 'Input all the nodes up front and then fill it in',
      type: 'guided',
    },
  ];

  const onContinue = useCallback(() => {
    if (!selected) return;
    if (!user) {
      dispatch(openLoginPrompt(true, history?.location?.pathname ?? '/'));
      return;
    }

    switch (selected?.type) {
      case 'duplicate': {
        onDuplicateMatrix(selected?.matrix);
        break;
      }
      case 'blank': {
        onCreateNewMatrix(false);
        break;
      }
      case 'guided': {
        onCreateNewMatrix(true);
        break;
      }
      default: {
        return;
      }
    }
  }, [user, dispatch, history, onDuplicateMatrix, onCreateNewMatrix, selected]);

  return (
    <StickySideModal
      className={classNames('NewMatrixModal', className)}
      onExit={onCancel}
    >
      <div className="NewMatrixModal-wrapper">
        <div className="NewMatrixModal-container">
          <div className="NewMatrixModal-header">Create a Matrix</div>
          <div className="NewMatrixModal-description">
            Customize an existing matrix to jumpstart your project, or build
            from the ground up
          </div>
          <div className="NewMatrixModal-main">
            <div className="NewMatrixModal-section">
              <div className="NewMatrixModal-section-header">
                <div className="NewMatrixModal-section-header-left">
                  <Icon icon="plus" />
                </div>
                <div className="NewMatrixModal-section-header-right">
                  Customize an existing matrix
                </div>
              </div>
            </div>

            <div className="NewMatrixModal-section">
              {publicMatrices
                .filter(pm => pm?.isTemplate)
                .map((pm, i) => (
                  <div
                    className={classNames('NewMatrixModal-template', {
                      active: pm.id === selected?.matrix?.id,
                    })}
                    key={i}
                  >
                    <div className="NewMatrixModal-template-left">
                      <div className="NewMatrixModal-template-image">
                        <img src={pm?.imageUrl} />
                      </div>
                      <div className="NewMatrixModal-template-main">
                        <div className="NewMatrixModal-template-name">
                          {pm?.templateName ?? pm?.name}
                        </div>

                        {pm.templateLocation ? (
                          <div className="NewMatrixModal-template-text">
                            <span className="NewMatrixModal-template-text-bold">
                              Location:
                            </span>{' '}
                            {pm.templateLocation}
                          </div>
                        ) : null}
                        {pm.templateEcosystem ? (
                          <div className="NewMatrixModal-template-text">
                            <span className="NewMatrixModal-template-text-bold">
                              Ecosystem:
                            </span>{' '}
                            {pm.templateEcosystem}
                          </div>
                        ) : null}
                        {pm.templatePurpose ? (
                          <div className="NewMatrixModal-template-text">
                            <span className="NewMatrixModal-template-text-bold">
                              Purpose:
                            </span>{' '}
                            {pm.templatePurpose}
                          </div>
                        ) : null}
                      </div>
                    </div>
                    <div className="NewMatrixModal-template-right">
                      <ButtonNew
                        buttonClasses="primary-button-small"
                        onClick={() =>
                          setSelected({ type: 'duplicate', matrix: pm })
                        }
                        label="Select"
                      />
                    </div>
                  </div>
                ))}
            </div>
            <div className="NewMatrixModal-section">
              <div className="NewMatrixModal-section-header">
                <div className="NewMatrixModal-section-header-left">
                  <Icon icon="plus" />
                </div>
                <div className="NewMatrixModal-section-header-right">
                  Create a new matrix
                </div>
              </div>
            </div>

            <div className="NewMatrixModal-section">
              {newMatrixOptions.map((m, i) => (
                <div
                  className={classNames('NewMatrixModal-template', {
                    active: m.type === selected?.type,
                  })}
                  key={i}
                >
                  <div className="NewMatrixModal-template-left">
                    <div className="NewMatrixModal-template-image"></div>
                    <div className="NewMatrixModal-template-main">
                      <div className="NewMatrixModal-template-name">
                        {m?.name}
                      </div>
                      <div className="NewMatrixModal-template-text">
                        {m?.description}
                      </div>
                    </div>
                  </div>
                  <div className="NewMatrixModal-template-right">
                    <ButtonNew
                      buttonClasses="primary-button-small"
                      onClick={() =>
                        setSelected({ type: m?.type, matrix: null })
                      }
                      label="Select"
                    />
                  </div>
                </div>
              ))}
            </div>
          </div>{' '}
        </div>
        <ModalFooter
          secondaryAction={onCancel}
          secondaryActionLabel={'Cancel'}
          primaryAction={onContinue}
          primaryActionDisabled={!selected}
          primaryActionLabel={user ? `Let's begin!` : 'Sign in to get started'}
        />
      </div>
    </StickySideModal>
  );
};

export default NewMatrixModal;
